import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import TabGroup from "@/components/ui/tab";
import Dropdown from "@/components/ui/dropdown";
import { ArrowLeft, ArrowRight } from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { FilterField, SortOption } from "@/types/component";
import type { ClientPoll } from "../types/client-polls.types";
import { clientSurveysApi } from "@/features/client-surveys";
import PollCard from "../components/PollCard";
import PollAnswerModal from "../components/PollAnswerModal";
import styles from "./ClientPollsPage.module.scss";

const PAGE_SIZE_OPTIONS = [12, 24, 48];
const SEARCH_DEBOUNCE_MS = 500;

/** UI sort value -> API sortBy, sortOrder */
function parseSortValue(
  value: string
): { sortBy: "startDate" | "endDate" | "title"; sortOrder: "asc" | "desc" } {
  switch (value) {
    case "newest":
      return { sortBy: "startDate", sortOrder: "desc" };
    case "oldest":
      return { sortBy: "startDate", sortOrder: "asc" };
    case "endDateDesc":
      return { sortBy: "endDate", sortOrder: "desc" };
    case "endDateAsc":
      return { sortBy: "endDate", sortOrder: "asc" };
    case "titleAsc":
      return { sortBy: "title", sortOrder: "asc" };
    case "titleDesc":
      return { sortBy: "title", sortOrder: "desc" };
    default:
      return { sortBy: "startDate", sortOrder: "desc" };
  }
}

/** Date range filter value to ISO 8601 start/end */
function dateRangeToParams(
  dateRange: unknown
): { startDate?: string; endDate?: string } {
  const arr = Array.isArray(dateRange) ? dateRange : null;
  if (!arr || arr.length < 2) return {};
  const start = arr[0] instanceof Date ? arr[0] : null;
  const end = arr[1] instanceof Date ? arr[1] : null;
  return {
    startDate: start ? start.toISOString() : undefined,
    endDate: end ? end.toISOString() : undefined,
  };
}

const ClientPollsPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation() as {
    state?: {
      educationId?: string;
      pollTitle?: string;
      status?: "pending" | "completed";
    };
  };

  const initialTab = location.state?.status === "completed" ? 1 : 0;

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sortValue, setSortValue] = useState("newest");
  const [polls, setPolls] = useState<ClientPoll[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [answerModalPoll, setAnswerModalPoll] = useState<ClientPoll | null>(null);

  const status: "pending" | "completed" = activeTab === 0 ? "pending" : "completed";

  useEffect(() => {
    const educationId = location.state?.educationId;
    if (educationId) {
      const isCompleted = location.state?.status === "completed";
      setAnswerModalPoll({
        id: educationId,
        title: location.state?.pollTitle ?? "",
        startDate: "",
        endDate: "",
        isCompleted,
        status: isCompleted ? "answered" : "pending",
      });
    }
  }, [location.state]);

  // Search debounce 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const tabs = [
    { label: t("clientPolls.tabs.pending"), content: null },
    { label: t("clientPolls.tabs.completed"), content: null },
  ];

  const filterFields: FilterField[] = [
    {
      name: "dateRange",
      label: t("clientPolls.filters.dateRange"),
      type: "daterange",
      placeholder: t("clientPolls.filters.dateRangePlaceholder"),
    },
  ];

  const sortOptions: SortOption[] = [
    { label: t("clientPolls.sort.newest"), value: "newest" },
    { label: t("clientPolls.sort.oldest"), value: "oldest" },
    { label: t("clientPolls.sort.endDateDesc"), value: "endDateDesc" },
    { label: t("clientPolls.sort.endDateAsc"), value: "endDateAsc" },
    { label: t("clientPolls.sort.titleAsc"), value: "titleAsc" },
    { label: t("clientPolls.sort.titleDesc"), value: "titleDesc" },
  ];

  useEffect(() => {
    setLoading(true);
    const { sortBy, sortOrder } = parseSortValue(sortValue);
    const { startDate, endDate } = dateRangeToParams(filterValues.dateRange);

    clientSurveysApi
      .getMySurveys({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status,
        startDate,
        endDate,
        sortBy,
        sortOrder,
      })
      .then(({ items, count }) => {
        setPolls(items);
        setTotalRecords(count);
      })
      .catch(() => {
        setPolls([]);
        setTotalRecords(0);
      })
      .finally(() => setLoading(false));
  }, [currentPage, pageSize, debouncedSearch, status, sortValue, filterValues]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setCurrentPage(1);
  };
  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };
  const handleSortChange = (option: SortOption) => {
    setSortValue(option.value);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handlePollAction = (poll: ClientPoll) => {
    if (poll.status === "pending" || poll.status === "answered") {
      setAnswerModalPoll(poll);
    }
  };

  const handleCloseAnswerModal = () => setAnswerModalPoll(null);
  const handleAnswerSuccess = () => {
    setAnswerModalPoll(null);
    setCurrentPage(1);
    setLoading(true);
    const { sortBy, sortOrder } = parseSortValue(sortValue);
    const { startDate, endDate } = dateRangeToParams(filterValues.dateRange);
    clientSurveysApi
      .getMySurveys({
        page: 1,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status,
        startDate,
        endDate,
        sortBy,
        sortOrder,
      })
      .then(({ items, count }) => {
        setPolls(items);
        setTotalRecords(count);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  return (
    <DashboardLayout>
      <Header title={t("clientPolls.title")} />

      <div className={styles.pageContainer}>
        <TabGroup
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={handleTabChange}
          orientation="horizontal"
        />

        <PageHeader
          searchPlaceholder={t("clientPolls.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={filterFields}
          onFilterChange={handleFilterChange}
          showSort
          sortOptions={sortOptions}
          selectedSortValue={sortValue}
          onSort={handleSortChange}
        />

        {loading ? (
          <div className={styles.emptyState}>
            {t("clientPolls.loading")}
          </div>
        ) : polls.length === 0 ? (
          <div className={styles.emptyState}>
            {t("clientPolls.noData")}
          </div>
        ) : (
          <>
            <div className={styles.pollGrid}>
              {polls.map((poll) => (
                <PollCard
                  key={poll.id}
                  title={poll.title}
                  startDate={poll.startDate}
                  endDate={poll.endDate}
                  status={poll.status}
                  onAction={() => handlePollAction(poll)}
                />
              ))}
            </div>

            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>{t("pagination.show")}</span>
                <Dropdown
                  onSelect={(v) => handlePageSizeChange(Array.isArray(v) ? v[0] : v)}
                  options={PAGE_SIZE_OPTIONS.map((size) => ({
                    label: size.toString(),
                    value: size.toString(),
                  }))}
                  menuPosition="top"
                  selectedLabel={pageSize.toString()}
                  value={pageSize.toString()}
                />
                <span className={styles.paginationDetails}>
                  {t("pagination.details", {
                    total: totalRecords,
                    start: (currentPage - 1) * pageSize + 1,
                    end: Math.min(currentPage * pageSize, totalRecords),
                  })}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(currentPage, totalPages, handlePageChange)}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ArrowRight height={16} width={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <PollAnswerModal
        educationId={answerModalPoll?.id ?? null}
        pollTitle={answerModalPoll?.title ?? ""}
        isOpen={!!answerModalPoll}
        readOnly={answerModalPoll?.status === "answered"}
        onClose={handleCloseAnswerModal}
        onSuccess={handleAnswerSuccess}
      />
    </DashboardLayout>
  );
};

export default ClientPollsPage;
